import React, { useState, useEffect } from 'react';
import { Bell, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Capacitor } from '@capacitor/core';
import { requestNotificationPermission, getWebPushToken, onForegroundMessage } from '../services/firebaseWeb';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const WebPushOptIn = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    // Don't show on native platforms (they use Capacitor push)
    if (Capacitor.isNativePlatform()) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    // Don't show if already granted or denied
    if (Notification.permission !== 'default') return;

    // Show after 10 seconds of app usage (best practice: timing)
    const dismissed = localStorage.getItem('push_prompt_dismissed');
    if (dismissed) {
      const dismissedAt = new Date(dismissed);
      const daysSince = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return; // Don't show again for 7 days
    }

    const timer = setTimeout(() => setShowPrompt(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  // Setup foreground message handler
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;

    try {
      const unsubscribe = onForegroundMessage((payload) => {
        const { title, body } = payload.notification || {};
        toast({
          title: title || 'Notification',
          description: body || ''
        });
      });
      return unsubscribe;
    } catch (e) {
      console.error('Foreground message setup error:', e);
    }
  }, []);

  const handleAccept = async () => {
    setRegistering(true);
    try {
      const result = await requestNotificationPermission();
      if (!result.granted) {
        toast({ title: 'Permission refusee', description: 'Vous pouvez activer les notifications dans les parametres du navigateur', variant: 'destructive' });
        setShowPrompt(false);
        return;
      }

      const token = await getWebPushToken();
      if (token) {
        await api.post('/notifications/register-device', { token, platform: 'web' });
        toast({ title: 'Notifications activees !', description: 'Vous recevrez des alertes importantes' });
      }
    } catch (error) {
      console.error('Push registration error:', error);
    } finally {
      setRegistering(false);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('push_prompt_dismissed', new Date().toISOString());
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 max-w-sm mx-auto z-50 animate-in slide-in-from-bottom duration-300" data-testid="push-opt-in">
      <div className="bg-gradient-to-br from-purple-900/95 to-pink-900/95 backdrop-blur-xl border border-pink-400/30 rounded-2xl p-5 shadow-2xl shadow-pink-500/20">
        <button onClick={handleDismiss} className="absolute top-3 right-3 text-pink-300/60 hover:text-white p-1" data-testid="push-dismiss">
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-400 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-base mb-1 flex items-center gap-1">
              Restez informee <Sparkles className="w-4 h-4 text-yellow-300" />
            </h3>
            <p className="text-pink-200 text-sm mb-3">
              Recevez des rappels pour vos regles, ovulation, pilule et des astuces sante personnalisees.
            </p>
            <div className="flex gap-2">
              <Button onClick={handleAccept} disabled={registering} size="sm"
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white text-sm px-4"
                data-testid="push-accept">
                {registering ? 'Activation...' : 'Activer'}
              </Button>
              <Button onClick={handleDismiss} variant="ghost" size="sm"
                className="text-pink-300 hover:text-white hover:bg-white/10 text-sm"
                data-testid="push-later">
                Plus tard
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebPushOptIn;
